import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  MdPerson,
  MdEmail,
  MdPhone,
  MdVerified,
  MdPending,
  MdSearch,
  MdLibraryMusic,
  MdEvent,
  MdDownload,
  MdFlag
} from 'react-icons/md';
import { getRegistrationsByActivity, exportRegistrationsExcel, exportFanMembersExcelBatch } from '../../api/registration';
import { getActivityDetails } from '../../api/activities';
import { getFanMemberByActivityId } from '../../config/fanMembersConfig';
import Notification from '../../components/Notification';
import DataTable from '../../components/DataTable';
import { formatWIBDateTime } from '../../utils/dateUtils';
import { formatCityWithCountry } from '../../utils/cityMappingUtils';

// Theme configuration
const THEMES = {
  event: {
    name: 'Event Management',
    primary: 'blue',
    gradient: 'from-blue-600 to-indigo-600',
    background: 'bg-gray-50',
    card: 'bg-white',
    text: 'text-gray-900',
    accent: 'text-blue-600',
    icon: MdEvent
  },
  fan: {
    name: 'Fan Management',
    primary: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    background: 'bg-slate-50',
    card: 'bg-white',
    text: 'text-slate-900',
    accent: 'text-purple-600',
    icon: MdLibraryMusic
  }
};

const RegistrationSearch = () => {
  const { activityId } = useParams();
  const [registrations, setRegistrations] = useState([]);
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [fanMemberDownloadingPart, setFanMemberDownloadingPart] = useState(null);
  const [success, setSuccess] = useState(null);

  // Determine theme based on activity type
  const theme = THEMES[activityData?.type] || THEMES.event;

  const activeFanMember = useMemo(
    () => (activityData ? getFanMemberByActivityId(activityData.id) : null),
    [activityData],
  );
  const ThemeIcon = theme.icon;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Reset filters when activity changes
        setCityFilter('all');
        setSearchTerm('');
        
        const [registrationsResponse, activityResponse] = await Promise.all([
          getRegistrationsByActivity(activityId),
          getActivityDetails(activityId)
        ]);

        if (registrationsResponse?.success && registrationsResponse?.data) {
          setRegistrations(registrationsResponse.data);
        } else if (registrationsResponse?.data) {
          setRegistrations(Array.isArray(registrationsResponse.data) ? registrationsResponse.data : []);
        }

        if (activityResponse?.success && activityResponse?.data) {
          setActivityData(activityResponse.data);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.response?.data?.error || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activityId]);

  // Get unique cities from registrations
  const uniqueCities = useMemo(() => {
    const cities = registrations
      .map(reg => reg?.city)
      .filter(city => city && city !== 'N/A' && city.trim() !== '')
      .filter((city, index, self) => self.indexOf(city) === index)
      .sort();
    return cities;
  }, [registrations]);

  // Filter registrations based on status and city
  const filteredRegistrations = useMemo(() => {
    let filtered = registrations;
    
    // Filter by status
    if (statusFilter === 'verified') {
      filtered = filtered.filter(reg => reg?.isVerified);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(reg => !reg?.isVerified);
    }
    
    // Filter by city
    if (cityFilter !== 'all') {
      filtered = filtered.filter(reg => reg?.city === cityFilter);
    }
    
    return filtered;
  }, [registrations, statusFilter, cityFilter]);

  // Statistics calculations
  const totalRegistrations = registrations.length;
  const verifiedCount = registrations.filter(reg => reg?.isVerified).length;
  const pendingCount = totalRegistrations - verifiedCount;

  // Define base table columns
  const baseColumns = [
    {
      header: 'User',
      accessor: 'user.name',
      searchFields: ['user.name', 'name', 'memberId'],
      render: (registration) => (
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${theme.gradient} flex items-center justify-center text-white font-semibold`}>
            {(registration?.user?.name || registration?.name)?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {registration?.user?.name || registration?.name || 'Unknown User'}
            </div>
            <div className="text-sm text-gray-500">
              {activeFanMember
                ? `Member ID: ${registration?.memberId || 'N/A'}`
                : `ID: ${registration?.id || 'N/A'}`}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Contact',
      accessor: 'user.email',
      searchFields: ['user.email', 'user.phoneNumber', 'email', 'phoneNumber'],
      render: (registration) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <MdEmail className="text-gray-400 text-sm" />
            <span className="text-sm text-gray-900">
              {registration?.user?.email || registration?.email || 'N/A'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <MdPhone className="text-gray-400 text-sm" />
            <span className="text-sm text-gray-600">
              {registration?.user?.phoneNumber || registration?.phoneNumber || 'N/A'}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Registration Date',
      accessor: 'createdAt',
      render: (registration) => {
        const { dateStr, timeStr } = formatWIBDateTime(registration?.createdAt);

        return (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{dateStr}</div>
            <div className="text-xs text-gray-500">{timeStr}</div>
          </div>
        );
      }
    },
    {
      header: 'Status',
      accessor: 'isVerified',
      render: (registration) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${registration?.isVerified
          ? 'bg-green-100 text-green-800'
          : 'bg-yellow-100 text-yellow-800'
          }`}>
          {registration?.isVerified ? (
            <>
              <MdVerified className="mr-1" />
              Verified
            </>
          ) : (
            <>
              <MdPending className="mr-1" />
              Pending
            </>
          )}
        </span>
      )
    }
  ];

  // Fan-member activities include a Kota column in registration forms
  const columns = activeFanMember
    ? [
        ...baseColumns.slice(0, 3), // User, Contact, Registration Date
        {
          header: 'City',
          accessor: 'city',
          render: (registration) => (
            <div className="flex items-center space-x-2">
              <MdFlag className="text-gray-400 text-sm" />
              <span className="text-sm text-gray-900">
                {formatCityWithCountry(registration?.city)}
              </span>
            </div>
          )
        },
        ...baseColumns.slice(3) // Status
      ]
    : baseColumns;

  // Export functionality (same as ActivityManagement)
  const handleExport = async () => {
    if (!activityData) return;
    
    try {
      setExporting(true);
      setError(null);
      const blob = await exportRegistrationsExcel(activityData.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `registrations-${activityData.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Excel export for "${activityData.name}" has been downloaded`);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to export Excel file');
    } finally {
      setExporting(false);
    }
  };

  const fanMemberBatchPlan = useMemo(() => {
    if (!activeFanMember) return null;
    if (statusFilter === 'pending') return { unsupported: true, totalParts: 0, parts: [] };

    const batchSize = 250;
    const onlyVerified = statusFilter === 'verified';

    // Use the same list shown in the table (after status/city filtering).
    const ids = filteredRegistrations
      .map((r) => Number(r?.id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((a, b) => a - b);

    const parts = [];
    const totalParts = Math.ceil(ids.length / batchSize);
    for (let part = 1; part <= totalParts; part++) {
      const startIndex = (part - 1) * batchSize;
      const endIndexExclusive = Math.min(part * batchSize, ids.length);
      const afterId = part === 1 ? 0 : ids[startIndex - 1];
      const limit = endIndexExclusive - startIndex;
      parts.push({
        part,
        afterId,
        limit,
        fromId: ids[startIndex],
        toId: ids[endIndexExclusive - 1],
      });
    }

    return { unsupported: false, batchSize, onlyVerified, totalParts, parts };
  }, [activeFanMember, filteredRegistrations, statusFilter]);

  const downloadFanMemberPart = async (partInfo) => {
    if (!activityData || !activeFanMember) return;
    if (!partInfo) return;

    try {
      setError(null);
      setSuccess(null);
      setFanMemberDownloadingPart(partInfo.part);

      const blob = await exportFanMembersExcelBatch(
        activityData.id,
        window.location.origin,
        fanMemberBatchPlan?.onlyVerified ?? true,
        partInfo.afterId,
        partInfo.limit,
        partInfo.part,
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeFanMember.slug}-members-${(fanMemberBatchPlan?.onlyVerified ?? true) ? 'verified' : 'all'}-part-${String(partInfo.part).padStart(3, '0')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Downloaded ${activeFanMember.displayName} export part ${partInfo.part}.`);
    } catch (err) {
      console.error('Fan member part download error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to download fan member export part');
    } finally {
      setFanMemberDownloadingPart(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Notification
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}
      {success && (
        <Notification
          type="success"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${theme.gradient} flex items-center justify-center`}>
            <MdSearch className="text-white text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Registration Search</h1>
            <p className="text-gray-600">Search and manage activity registrations</p>
          </div>
        </div>

        {activityData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <ThemeIcon className={theme.accent} />
              <div>
                <h3 className="font-medium text-gray-900">{activityData.name}</h3>
                <p className="text-sm text-gray-600">{theme.name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Registrations</p>
                <p className="text-2xl font-bold text-blue-800">{totalRegistrations}</p>
              </div>
              <MdPerson className="text-2xl text-blue-600" />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Verified</p>
                <p className="text-2xl font-bold text-green-800">{verifiedCount}</p>
              </div>
              <MdVerified className="text-2xl text-green-600" />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-800">{pendingCount}</p>
              </div>
              <MdPending className="text-2xl text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Registration List</h2>
            <p className="text-gray-600 mt-1">
              Search and filter through all registrations
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified Only</option>
              <option value="pending">Pending Only</option>
            </select>

            {(activeFanMember || uniqueCities.length > 0) && (
              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Cities</option>
                {uniqueCities.map((city) => (
                  <option key={city} value={city}>
                    {formatCityWithCountry(city)}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={handleExport}
              disabled={exporting || !activityData}
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <MdDownload className="mr-2" />
                  Export Excel
                </>
              )}
            </button>
          </div>
        </div>

        {activeFanMember && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold text-emerald-900">{activeFanMember.displayName} Excel export (batched)</div>
                <div className="text-sm text-emerald-800 mt-1">
                  Download parts one by one to avoid timeouts. Uses the current filters in the table.
                </div>
                {fanMemberBatchPlan?.unsupported && (
                  <div className="text-sm text-emerald-900 mt-2">
                    Pending-only export is not supported here. Switch status to “All Status” or “Verified Only”.
                  </div>
                )}
              </div>
              {!fanMemberBatchPlan?.unsupported && (
                <div className="text-sm text-emerald-900">
                  Parts: <span className="font-semibold">{fanMemberBatchPlan?.totalParts ?? 0}</span>
                </div>
              )}
            </div>

            {!fanMemberBatchPlan?.unsupported && (fanMemberBatchPlan?.parts?.length ?? 0) > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {fanMemberBatchPlan.parts.map((p) => (
                  <div key={p.part} className="bg-white border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">Batch {p.part}</div>
                      <div className="text-xs text-gray-600">
                        {p.limit} rows (ID {p.fromId}–{p.toId})
                      </div>
                    </div>
                    <button
                      onClick={() => downloadFanMemberPart(p)}
                      disabled={fanMemberDownloadingPart !== null}
                      className="shrink-0 inline-flex items-center px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {fanMemberDownloadingPart === p.part ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Downloading
                        </>
                      ) : (
                        <>
                          <MdDownload className="mr-2" />
                          Download
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!fanMemberBatchPlan?.unsupported && (fanMemberBatchPlan?.parts?.length ?? 0) === 0 && (
              <div className="mt-3 text-sm text-emerald-900">No rows to export for the current filters.</div>
            )}
          </div>
        )}

        <DataTable
          data={filteredRegistrations}
          columns={columns}
          searchTerm={searchTerm}
          itemsPerPage={itemsPerPage}
          emptyMessage="No registrations found matching your criteria"
        />
      </div>
    </div>
  );
};

export default RegistrationSearch;